import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { Buffer } from 'buffer';
import { GetBoardingCertificateRequest } from 'src/sdk-servicing';
import { ApiErrorResponseModel, AswContextType, DeviceType } from '@lib/interfaces';
import {
  AswContextStoreService,
  AswMasterService,
  CommonLibService,
  PageLoadingService,
  SystemDateService,
} from '@lib/services';
import { ServicingBaseClass } from '@common/components';
import { GetBoardingCertificateResultStoreService } from '.';

@Injectable()
export class BoardingCertificateService extends ServicingBaseClass {
  /** エラーが発生している搭乗証明書取得APIのレスポンス通知 */
  get getBoardingCertificateFailure$(): Observable<ApiErrorResponseModel | null> {
    return this._getBoardingCertificateResultStoreService.failure$.pipe(
      withLatestFrom(this._commonLibService.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state)
    );
  }

  /** PDFを表示する別タブ */
  private pdfWindow: WindowProxy | null = null;

  /**
   * 端末識別がSPであるかを取得する
   * @returns true: SP / false: SP以外
   */
  get isDeviceTypeSmartPhone(): boolean {
    return this._aswContextStoreService.aswContextData[AswContextType.DEVICE_TYPE] === DeviceType.SMART_PHONE;
  }

  /**
   * 操作オフィスコードに紐づく空港現地時間を取得する
   * @returns 操作オフィスコードに紐づく空港現地時間
   */
  get pointOfSaleLocalDate(): Date {
    return this._systemDateService.getAirportLocalDate(this._aswContextStoreService.aswContextData.pointOfSaleId);
  }

  constructor(
    private _systemDateService: SystemDateService,
    private _commonLibService: CommonLibService,
    private _pageLoadingService: PageLoadingService,
    private _aswContextStoreService: AswContextStoreService,
    private _aswMasterService: AswMasterService,
    private _getBoardingCertificateResultStoreService: GetBoardingCertificateResultStoreService
  ) {
    super();

    this.watchGetBoardingCertificateSuccess();
    this.watchGetBoardingCertificateFailure();
  }

  override destroy(): void {}

  /**
   * 基準日から指定年数の応当日を取得する
   * @param baseDate 基準日
   * @param year 指定年数
   * @returns Date型応当日
   */
  getYearAdded(baseDate: Date, year: number): Date {
    return this.getMonthAdded(baseDate, year * 12);
  }

  /**
   * 基準日から指定月数の応当日を取得する
   * @param baseDate 基準日
   * @param month 指定月数
   * @returns Date型応当日
   */
  getMonthAdded(baseDate: Date, month: number): Date {
    const date = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1 + month, 0);
    const lastDay = date.getDate();

    let day = baseDate.getDate();
    if (month > 0) {
      if (day <= lastDay) {
        date.setDate(day - 1);
      }
    } else if (month < 0) {
      if (day > lastDay) {
        day = lastDay;
      }

      date.setDate(day + 1);
    }

    return date;
  }

  /**
   * 日時から年月日を取得する
   * @param date Date型日付
   * @returns string型年月日
   */
  getYearMonthDay(date: Date): string {
    const yearMonth = this.getYearMonth(date);
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${yearMonth}${day}`;
  }

  /**
   * 日時から年月を取得する
   * @param date Date型日付
   * @returns string型年月
   */
  getYearMonth(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');

    return `${year}${month}`;
  }

  /**
   * 搭乗証明書PDF取得APIをリクエストする
   * @param parameter 搭乗証明書表示条件
   */
  requestBoardingCertificate(parameter: GetBoardingCertificateRequest): void {
    this._pageLoadingService.startLoading();

    this.pdfWindow = this.isDeviceTypeSmartPhone ? null : open('');
    this._getBoardingCertificateResultStoreService.request(parameter);
  }

  /**
   * 搭乗証明書PDF取得APIレスポンス正常取得
   */
  private watchGetBoardingCertificateSuccess(): void {
    this.subscribeService(this._getBoardingCertificateResultStoreService.success$, (state) => {
      const pdfData = state.model?.data.pdfData;
      if (pdfData) {
        const blob = new Blob([Buffer.from(pdfData, 'base64')], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const pdfFileName = this._aswMasterService.getMPropertyByKey(
          'reportForm',
          'downloadFileName.servicing.boardingCertificate'
        );

        if (this.isDeviceTypeSmartPhone) {
          const a = document.createElement('a');
          a.download = pdfFileName;
          a.href = url;
          a.click();
          a.remove();

          URL.revokeObjectURL(url);
        } else {
          const pdfDocument = this.pdfWindow?.document;
          if (pdfDocument) {
            pdfDocument.title = pdfFileName;

            const embed = pdfDocument.createElement('embed');
            embed.src = `${url}#toolbar=1`;
            embed.style.width = '100vw';
            embed.style.height = '100vh';
            pdfDocument.body.append(embed);
          }
        }
      }

      this.pdfWindow = null;

      this._pageLoadingService.endLoading();
    });
  }

  /**
   * 搭乗証明書PDF取得APIレスポンス異常取得
   */
  private watchGetBoardingCertificateFailure(): void {
    this.subscribeService(this.getBoardingCertificateFailure$, () => {
      this.pdfWindow?.close();
      this.pdfWindow = null;

      this._pageLoadingService.endLoading();
    });
  }
}
